// Bill status as the collections team means it, plus the ordering both
// collections pages share.
//
// The stored Bill.status is not trustworthy for display: recording a payment
// receipt flips it to "Collected" whatever the amount, so a ₹55,000 bill with a
// ₹47,500 receipt reads "Collected" while ₹7,500 is still owed. Everything the
// user sees — the badge, the Pending/Collected filter, the stat cards and the
// sort — goes through effectiveStatus() instead, which asks the amounts.
//
// Shared so Payment Collections and Current Payment Collections can't disagree
// about which bills are still outstanding.

import { compareByDutyThenName } from '../constants/shifts';

export interface BillLike {
  total_amount: number;
  amount_received: number;
  status: string;
  bill_type?: string | null;
  duty_type?: string | null;
  client_name?: string | null;
  patient_name: string;
}

// Rounding slack — money arrives as floats, and a 0.004 residue is paid off.
const EPSILON = 0.009;

/** What is still owed on the bill, whether or not anything has been received. */
export const outstandingOf = (b: BillLike): number =>
  Math.max((Number(b.total_amount) || 0) - (Number(b.amount_received) || 0), 0);

/**
 * What has been settled against the bill.
 *
 * Derived as billed − outstanding rather than read from amount_received, so
 * that billed = collected + pending holds for every row and therefore for
 * every subtotal. Two cases depend on it:
 *
 *  - a consumable return is a credit note (negative total, no receipt). It owes
 *    nothing, so it counts as fully settled and its negative amount reduces
 *    both the billed and the collected column, leaving pending untouched.
 *  - an overpayment counts only up to what was billed; the excess is not money
 *    collected *against this bill*.
 */
export const collectedOf = (b: BillLike): number =>
  (Number(b.total_amount) || 0) - outstandingOf(b);

/**
 * The status to show. Anything still owed reads Pending, however the row was
 * stored; only a bill with nothing outstanding keeps its stored status (which
 * distinguishes Collected from Processed).
 */
export const effectiveStatus = (b: BillLike): string => {
  // A consumable return is a credit note, not money to collect — it carries a
  // negative total and is settled by adjusting it against the client's account.
  // Left to the rule below it read "Pending" forever and sat in the chase list
  // among bills that genuinely owe.
  if (b.bill_type === 'consumable_return') return 'Completed';
  return outstandingOf(b) > EPSILON ? 'Pending' : (b.status || 'Collected');
};

export const isCollected = (b: BillLike): boolean => effectiveStatus(b) !== 'Pending';

/**
 * The name the Client column shows, and so the one the A→Z step reads by.
 *
 * Trimmed: some stored client names carry a stray leading space, which sorts
 * before every letter and threw the row to the top of the list. HTML collapses
 * that space when rendering, so the name looks fine on screen and only the
 * ordering gives it away. patient_clients is not normalised at write time (the
 * way nurse/physio names are), so this has to defend itself.
 */
export const clientLabel = (b: BillLike): string =>
  (b.client_name || b.patient_name || '').trim();

/**
 * The shared row order for both collections pages:
 *   1. anything settled drops below anything still owed
 *   2. then duty type — 08 Hrs, 12 Dual, 12 Day, 12 Night, 24 Hrs
 *   3. then A→Z on the client name, inside each of those groups
 */
export const compareCollectionRows = (a: BillLike, b: BillLike): number =>
  (isCollected(a) ? 1 : 0) - (isCollected(b) ? 1 : 0)
  || compareByDutyThenName(
    { duty_type: a.duty_type, name: clientLabel(a) },
    { duty_type: b.duty_type, name: clientLabel(b) },
  );
